<template>
  <div class="relative z-[200]">
    <Overlay :visible="isOpen" @click="close" />
    <Drawer
      class="w-full sm:w-[600px] p-4 pt-0 pb-8 flex flex-col h-[100dvh]"
      data-test-id="hubspot-form-sidebar"
      :is-open="isOpen"
      @click:close="close"
    >
      <template #header>
        <div
          class="h-14 flex items-center pr-8 text-lg font-medium text-[#666971]"
        >
          {{ title }}
        </div>
      </template>
      <div v-if="form" class="relative flex-1">
        <div
          v-if="status === 'loading'"
          class="absolute inset-x-0 top-12 flex justify-center text-teal-900"
          role="status"
        >
          <Loader2 class="animate-spin" :size="32" aria-hidden="true" />
          <span class="sr-only">{{ t('general.loading') }}</span>
        </div>
        <div
          v-else-if="status === 'error'"
          class="flex flex-col items-center gap-4 pt-12 text-center"
        >
          <p>{{ t('general.hubspotForm.loadError') }}</p>
          <Button :link="formUrl" open-new-tab>
            {{ t('general.hubspotForm.openInNewTab') }}
          </Button>
        </div>
        <!-- HubSpot's v4 embed script picks this up and renders an auto-sizing iframe -->
        <div
          :key="form.formId"
          ref="formFrameRef"
          class="hs-form-frame"
          :data-region="form.region"
          :data-form-id="form.formId"
          :data-portal-id="form.portalId"
        />
      </div>
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import { Loader2 } from 'lucide-vue-next'

// Ad blockers commonly block hsforms.net; fall back to the share link then.
const LOAD_TIMEOUT_MS = 15000

const { t } = useI18n()
const { isOpen, close, formUrl, title } = useHubspotFormSidebarUiState()

const form = computed(() => parseHubspotShareUrl(formUrl.value))
const status = ref<'loading' | 'ready' | 'error'>('loading')
const formFrameRef = ref<HTMLElement | null>(null)

const loadEmbedScript = (src: string) => {
  if (document.querySelector(`script[src="${src}"]`)) return

  const script = document.createElement('script')
  script.src = src
  script.async = true
  script.addEventListener(
    'error',
    () => {
      console.error(`Failed to load HubSpot form embed script: ${src}`)
      status.value = 'error'
    },
    { once: true }
  )
  document.head.appendChild(script)
}

watch(formFrameRef, (el, _previous, onCleanup) => {
  if (!el || !form.value) return

  status.value = 'loading'

  // The embed sets the container height once the form has rendered.
  const observer = new ResizeObserver(() => {
    if (el.offsetHeight > 0) {
      status.value = 'ready'
      observer.disconnect()
    }
  })
  observer.observe(el)

  const timeout = setTimeout(() => {
    if (status.value === 'loading') status.value = 'error'
  }, LOAD_TIMEOUT_MS)

  onCleanup(() => {
    observer.disconnect()
    clearTimeout(timeout)
  })

  loadEmbedScript(form.value.embedScriptUrl)
})
</script>
